import { Die, YatzyScores } from '../types/game';
import { calculatePossibleScore } from './gameLogic';
import { calculateMaxiScore, maxiScoreCategories } from './maxiGameLogic';
import { StrategyAdvisor } from './strategyAdvisor';

/**
 * AI-motståndare för lokal multiplayer.
 *
 * Klassiskt läge återanvänder StrategyAdvisor (samma hjärna som AI-assistenten);
 * Maxi använder en egen heuristik eftersom advisorn bara kan klassiska
 * kategorier. Svårighetsgraden styr hur ofta AI:n medvetet spelar suboptimalt.
 */

export type AILevel = 'easy' | 'medium' | 'hard';

export const AI_LEVEL_NAMES: Record<AILevel, string> = {
  easy: 'AI Lätt',
  medium: 'AI Medel',
  hard: 'AI Svår',
};

/** Sannolikhet att AI:n avviker från bästa draget. */
const MISTAKE_RATE: Record<AILevel, number> = { easy: 0.45, medium: 0.18, hard: 0 };

export type AITurnDecision =
  | { action: 'score'; category: string }
  | { action: 'keep'; keepIndices: number[] };

/** Ungefärligt maxvärde per kategori – används för att stryka "billigast" först. */
const MAX_POTENTIAL: Record<string, number> = {
  // Klassiskt (5 tärningar) – maxi-upper skrivs över nedan där det skiljer.
  ones: 5, twos: 10, threes: 15, fours: 20, fives: 25, sixes: 30,
  onePair: 12, twoPairs: 22, threeOfAKind: 18, fourOfAKind: 24,
  smallStraight: 15, largeStraight: 20, fullHouse: 28, chance: 30, yatzy: 50,
  // Maxi-specifika
  fiveOfAKind: 30, fullStraight: 21, hus: 33, torn: 34,
};

function countValues(values: number[]): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const v of values) counts[v] = (counts[v] || 0) + 1;
  return counts;
}

function emptyCategories(scores: Record<string, number | null>, isMaxi: boolean): string[] {
  const order = isMaxi
    ? maxiScoreCategories.map(c => c.key as string)
    : Object.keys(MAX_POTENTIAL).filter(k => !['fiveOfAKind', 'fullStraight', 'hus', 'torn'].includes(k));
  return order.filter(k => scores[k] === null);
}

function scoreFor(dice: Die[], category: string, isMaxi: boolean): number {
  return isMaxi
    ? calculateMaxiScore(dice as any, category as any)
    : calculatePossibleScore(dice, category as any);
}

/** Slumpa fram om AI:n ska "göra fel" givet nivån. */
function shouldBlunder(level: AILevel, rng: () => number): boolean {
  return rng() < MISTAKE_RATE[level];
}

/** Vilka tärningar (index) bör behållas i Maxi? Enkel men rimlig heuristik. */
function maxiKeepIndices(dice: Die[], scores: Record<string, number | null>): number[] {
  const values = dice.map(d => d.value);
  const counts = countValues(values);

  // Störst grupp av lika – vid lika storlek: högst valör.
  const groups = Object.entries(counts)
    .map(([v, c]) => ({ value: parseInt(v, 10), count: c }))
    .sort((a, b) => b.count - a.count || b.value - a.value);
  const best = groups[0];

  // Stegepotential: 4+ unika i rad och någon stege ledig → behåll en av varje.
  const straightOpen = scores.smallStraight === null || scores.largeStraight === null || scores.fullStraight === null;
  const unique = Array.from(new Set(values)).sort((a, b) => a - b);
  let bestRun: number[] = [];
  let run: number[] = [];
  for (const v of unique) {
    if (run.length === 0 || v === run[run.length - 1] + 1) run.push(v);
    else run = [v];
    if (run.length > bestRun.length) bestRun = [...run];
  }
  if (straightOpen && bestRun.length >= 4 && best.count < 3) {
    const wanted = new Set(bestRun);
    const kept = new Set<number>();
    const keep: number[] = [];
    values.forEach((v, i) => {
      if (wanted.has(v) && !kept.has(v)) { kept.add(v); keep.push(i); }
    });
    return keep;
  }

  if (best.count >= 2) {
    return values.map((v, i) => (v === best.value ? i : -1)).filter(i => i >= 0);
  }
  // Inget mönster: kasta om allt utom högsta tärningen.
  const maxVal = Math.max(...values);
  return [values.indexOf(maxVal)];
}

/** Bästa kategori just nu (störst poäng; vid 0 överallt: stryk billigast). */
function pickBestCategory(
  dice: Die[],
  scores: Record<string, number | null>,
  isMaxi: boolean
): { best: string; secondBest: string | null } {
  const empty = emptyCategories(scores, isMaxi);
  const ranked = empty
    .map(k => ({ key: k, score: scoreFor(dice, k, isMaxi) }))
    .sort((a, b) => b.score - a.score || (MAX_POTENTIAL[b.key] || 0) - (MAX_POTENTIAL[a.key] || 0));

  const allZero = ranked.length > 0 && ranked[0].score === 0;
  if (allZero) {
    // Stryk kategorin med lägst potential (spara yatzy/chans så länge det går).
    const cheapest = [...ranked].sort((a, b) => (MAX_POTENTIAL[a.key] || 0) - (MAX_POTENTIAL[b.key] || 0));
    return { best: cheapest[0].key, secondBest: cheapest[1]?.key ?? null };
  }
  return { best: ranked[0].key, secondBest: ranked[1]?.key ?? null };
}

/** Ska AI:n säkra en toppkombination direkt istället för att kasta igen? */
function shouldScoreEarly(dice: Die[], scores: Record<string, number | null>, isMaxi: boolean): string | null {
  const check = (key: string, minScore: number) =>
    scores[key] === null && scoreFor(dice, key, isMaxi) >= minScore ? key : null;
  return (
    check('yatzy', isMaxi ? 100 : 50) ||
    check('fullStraight', 21) ||
    check('largeStraight', 20) ||
    check('smallStraight', 15)
  );
}

/**
 * Beslutar AI:ns nästa drag när minst ett kast är gjort och kast återstår:
 * antingen "score" (säkra en toppkombination / tvingad kategori vid full pott)
 * eller "keep" med tärningsindex som ska låsas inför nästa kast.
 */
export function decideTurnAction(
  dice: Die[],
  scores: Record<string, number | null>,
  rollsLeft: number,
  upperSectionScore: number,
  isMaxi: boolean,
  level: AILevel,
  forcedNext?: string | null,
  rng: () => number = Math.random
): AITurnDecision {
  // Tvingande läge: sikta på den tvingade kategorin.
  if (forcedNext) {
    const current = scoreFor(dice, forcedNext, isMaxi);
    const max = MAX_POTENTIAL[forcedNext] || 0;
    if (current >= max * 0.8 && current > 0) return { action: 'score', category: forcedNext };
    // Behåll tärningar som bidrar till den tvingade kategorin (övre sektionen),
    // annars generisk keep-heuristik.
    const faceMap: Record<string, number> = { ones: 1, twos: 2, threes: 3, fours: 4, fives: 5, sixes: 6 };
    const face = faceMap[forcedNext];
    if (face) {
      const keep = dice.map((d, i) => (d.value === face ? i : -1)).filter(i => i >= 0);
      return { action: 'keep', keepIndices: keep };
    }
  }

  const early = shouldScoreEarly(dice, scores, isMaxi);
  if (early && !shouldBlunder(level, rng)) return { action: 'score', category: early };

  let keep: number[];
  if (isMaxi) {
    keep = maxiKeepIndices(dice, scores);
  } else {
    const advice = StrategyAdvisor.analyzePosition(dice, scores as unknown as YatzyScores, rollsLeft, upperSectionScore);
    keep = advice.diceToKeep ?? [];
  }

  if (shouldBlunder(level, rng)) {
    // Suboptimalt: släpp en av de behållna tärningarna (eller behåll en extra).
    if (keep.length > 0) keep = keep.slice(0, keep.length - 1);
    else if (dice.length > 0) keep = [Math.floor(rng() * dice.length)];
  }
  return { action: 'keep', keepIndices: keep };
}

/** Väljer kategori när kasten är slut (eller AI:n bestämt sig för att säkra). */
export function decideFinalCategory(
  dice: Die[],
  scores: Record<string, number | null>,
  upperSectionScore: number,
  isMaxi: boolean,
  level: AILevel,
  forcedNext?: string | null,
  rng: () => number = Math.random
): string {
  if (forcedNext) return forcedNext;

  if (!isMaxi) {
    const advice = StrategyAdvisor.analyzePosition(dice, scores as unknown as YatzyScores, 0, upperSectionScore);
    if (advice.category && !shouldBlunder(level, rng)) return advice.category;
  }

  const { best, secondBest } = pickBestCategory(dice, scores, isMaxi);
  if (secondBest && shouldBlunder(level, rng)) return secondBest;
  return best;
}
